import React, { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Package, Search, X, Plus, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * ItemPickerCombo — searchable dropdown for selecting an item from the catalog.
 *
 * Props:
 *   items     — full catalog array [{id, name, description, rate, tax_rate, item_type}]
 *   filterType — optional "sales" | "purchase" — only include items matching (+ "both")
 *   currency   — display currency for the rate column
 *   onPick     — (item) => void — user picked an item (or typed Enter on search match)
 *   onCreate   — optional (item) => void — called after inline-create so parent can
 *                 refresh its catalog state
 *   currentRate — optional number — prefills rate on inline-create (e.g. the current line's price)
 *   currentTaxRate — optional number — prefills tax on inline-create
 *   trigger    — optional custom trigger node; defaults to "+" button
 *   testId     — base data-testid prefix, e.g. "invoice-line-0"
 */
export default function ItemPickerCombo({ items = [], filterType, currency = "KYD", onPick, onCreate, currentRate, currentTaxRate, trigger, testId = "item-pick" }) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const [creating, setCreating] = useState(false);

    const filtered = useMemo(() => {
        let list = items.filter((i) => i.active !== false);
        if (filterType) list = list.filter((i) => i.item_type === filterType || i.item_type === "both");
        const qq = q.trim().toLowerCase();
        if (qq) list = list.filter((i) => (
            (i.name || "").toLowerCase().includes(qq) ||
            (i.description || "").toLowerCase().includes(qq) ||
            String(i.rate || "").includes(qq)
        ));
        return list.slice(0, 80);
    }, [items, filterType, q]);

    const exactMatch = useMemo(() => {
        const qq = q.trim().toLowerCase();
        if (!qq) return null;
        return items.find((i) => (i.name || "").toLowerCase() === qq) || null;
    }, [items, q]);

    const fmtRate = (r) => new Intl.NumberFormat("en-US", { style: "currency", currency, maximumFractionDigits: 2 }).format(Number(r) || 0);

    const pick = (it) => {
        onPick?.(it);
        setOpen(false);
        setQ("");
    };

    const createInline = async () => {
        const name = q.trim();
        if (!name || exactMatch || creating) return;
        setCreating(true);
        try {
            const payload = {
                name,
                item_type: filterType === "purchase" ? "purchase" : filterType === "sales" ? "sales" : "both",
                rate: Number(currentRate) > 0 ? Number(currentRate) : 0,
                tax_rate: Number(currentTaxRate) >= 0 ? Number(currentTaxRate) : 0,
                active: true,
            };
            const { data } = await api.post("/items", payload);
            toast.success(`Added "${data.name}" to your catalog`);
            onCreate?.(data);
            pick(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setCreating(false);
        }
    };

    const canCreate = q.trim().length > 0 && !exactMatch;

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                {trigger || (
                    <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="w-11 px-0 justify-center shrink-0 border-sand-300"
                        data-testid={`${testId}-trigger`}
                        title="Pick from item catalog"
                    >
                        <Package className="h-4 w-4 text-secondary" />
                    </Button>
                )}
            </PopoverTrigger>
            <PopoverContent className="w-[340px] p-0" align="end" sideOffset={6}>
                <div className="p-2 border-b border-sand-200">
                    <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            autoFocus
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search or add a new item…"
                            className="pl-8 pr-8 h-9"
                            data-testid={`${testId}-search`}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    if (filtered[0]) pick(filtered[0]);
                                    else if (canCreate) createInline();
                                }
                                if (e.key === "Escape") setOpen(false);
                            }}
                        />
                        {q && (
                            <button
                                type="button"
                                onClick={() => setQ("")}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-ocean-700"
                                aria-label="Clear search"
                            >
                                <X className="h-3.5 w-3.5" />
                            </button>
                        )}
                    </div>
                </div>
                <div className="max-h-72 overflow-y-auto py-1" data-testid={`${testId}-list`}>
                    {filtered.length === 0 && !canCreate && (
                        <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                            {items.length === 0 ? "No items in your catalog yet. Add one by typing the name above." : "No items match this search."}
                        </div>
                    )}
                    {filtered.map((it) => (
                        <button
                            type="button"
                            key={it.id}
                            onClick={() => pick(it)}
                            className="w-full text-left px-3 py-2 hover:bg-sand-100 focus:bg-sand-100 focus:outline-none flex items-start gap-3"
                            data-testid={`${testId}-option-${it.id}`}
                        >
                            <div className="flex-1 min-w-0">
                                <div className="font-medium text-sm text-ocean-700 truncate">{it.name}</div>
                                {it.description && (
                                    <div className="text-xs text-muted-foreground truncate">{it.description}</div>
                                )}
                                <div className="text-[10px] uppercase tracking-wide text-muted-foreground mt-0.5">
                                    {it.item_type === "both" ? "Sales / Purchase" : it.item_type}
                                    {it.tax_rate > 0 && ` · ${it.tax_rate}% tax`}
                                </div>
                            </div>
                            <div className="font-mono text-sm text-ocean-700 tabular-nums whitespace-nowrap">
                                {fmtRate(it.rate)}
                            </div>
                        </button>
                    ))}
                </div>
                {canCreate && (
                    <button
                        type="button"
                        onClick={createInline}
                        disabled={creating}
                        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 border-t border-sand-200 bg-secondary/5 hover:bg-secondary/10 focus:bg-secondary/10 focus:outline-none text-left disabled:opacity-60"
                        data-testid={`${testId}-create`}
                    >
                        <div className="flex items-center gap-2 min-w-0">
                            {creating ? (
                                <Loader2 className="h-4 w-4 text-secondary animate-spin shrink-0" />
                            ) : (
                                <Plus className="h-4 w-4 text-secondary shrink-0" />
                            )}
                            <div className="min-w-0">
                                <div className="text-sm font-medium text-secondary-dark truncate">Create &ldquo;{q.trim()}&rdquo;</div>
                                <div className="text-[10px] text-muted-foreground">
                                    {filterType === "sales" ? "Sales item" : filterType === "purchase" ? "Purchase item" : "Item"}
                                    {Number(currentRate) > 0 && ` · default rate ${fmtRate(currentRate)}`}
                                </div>
                            </div>
                        </div>
                        <span className="text-[10px] uppercase tracking-wider text-secondary-dark font-semibold shrink-0">Enter</span>
                    </button>
                )}
            </PopoverContent>
        </Popover>
    );
}
